import {
  buildCanvasPublicUrl,
  useCanvasById,
  usePublishCanvas,
  useUnpublishCanvas,
} from "@/api/canvas";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { useCanvasPermissions } from "@/pages/canvas/hooks/useCanvasPermissions";
import { cn } from "@/utils";
import {
  trackPublicLinkCopied,
  trackPublicLinkToggled,
} from "@/utils/analytics";
import {
  type Canvas,
  type CanvasPublishedStatus,
  resolveCanvasViewUrl,
} from "@cohesive/api-client";
import { Spinner } from "@cohesive/ui-components";
import { isAxiosError } from "axios";
import { formatDistanceToNow } from "date-fns";
import { Copy, Link2 } from "lucide-react";
import { useCallback } from "react";
import toast from "react-hot-toast";

/**
 * Whether the public link is considered "on" for the share toggle / Copy link.
 * A prior snapshot stays live through republish (`publishing`) and retryable
 * `failed`. First publish (no `published_at` yet) stays off until the CDN
 * artifact exists — otherwise Copy link would hand out a dead URL.
 */
export function isCanvasPublicLinkOn(
  status: CanvasPublishedStatus | undefined,
  publishedAt: string | undefined,
): boolean {
  if (status === "published") return true;
  if ((status === "publishing" || status === "failed") && publishedAt)
    return true;
  return false;
}

function publishErrorMessage(error: unknown): string {
  if (!isAxiosError(error)) {
    return "Failed to publish canvas. Please try again.";
  }
  const status = error.response?.status;
  if (status === 409) return "A publish is already in progress.";
  if (status === 422) return "Archived canvases cannot be published.";
  if (status === 403)
    return "You don't have permission to publish this canvas.";
  return "Failed to publish canvas. Please try again.";
}

function unpublishErrorMessage(error: unknown): string {
  if (!isAxiosError(error)) {
    return "Failed to unpublish canvas. Please try again.";
  }
  if (error.response?.status === 403) {
    return "You don't have permission to unpublish this canvas.";
  }
  return "Failed to unpublish canvas. Please try again.";
}

function linkSubtitle(
  status: CanvasPublishedStatus | undefined,
  publishedAt: string | undefined,
  linkOn: boolean,
): string {
  if (status === "publishing") return "Publishing…";
  if (!linkOn) return "Only people with access can open";
  if (status === "failed") {
    return "Last publish failed — prior link still live";
  }
  if (publishedAt) {
    return `Published ${formatDistanceToNow(new Date(publishedAt), {
      addSuffix: true,
    })}`;
  }
  return "Link sharing is on";
}

export function CanvasLinkAccessSection({
  canvas: canvasProp,
}: {
  canvas: Canvas;
}) {
  // Prefer live query cache so WS `canvas.updated` publish transitions refresh
  // the band without remounting the dialog.
  const { data: liveCanvas } = useCanvasById(canvasProp.id, {
    initialData: canvasProp,
  });
  const canvas = liveCanvas ?? canvasProp;
  const { canEdit } = useCanvasPermissions(canvas.id);

  const publishCanvas = usePublishCanvas({
    onError: (error) => toast.error(publishErrorMessage(error)),
  });
  const unpublishCanvas = useUnpublishCanvas({
    onError: (error) => toast.error(unpublishErrorMessage(error)),
  });

  const status = canvas.published_status;
  const publishedAt = canvas.published_at;
  const linkOn = isCanvasPublicLinkOn(status, publishedAt);
  const isPublishing = status === "publishing";
  const failedFirstPublish = status === "failed" && !publishedAt;
  const isMutating = publishCanvas.isPending || unpublishCanvas.isPending;
  const publicUrl = resolveCanvasViewUrl(canvas, () =>
    buildCanvasPublicUrl(canvas.id),
  );
  const showUpdate =
    canEdit &&
    linkOn &&
    !isMutating &&
    !isPublishing &&
    (status === "published" || (status === "failed" && !!publishedAt));

  const handleToggle = useCallback(
    (next: boolean) => {
      if (!canEdit || isMutating || isPublishing) return;
      trackPublicLinkToggled({ objectKind: "canvas", on: next });
      if (next) {
        publishCanvas.mutate(canvas.id);
      } else {
        unpublishCanvas.mutate(canvas.id);
      }
    },
    [
      canEdit,
      isMutating,
      isPublishing,
      canvas.id,
      publishCanvas,
      unpublishCanvas,
    ],
  );

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(publicUrl);
      trackPublicLinkCopied({ objectKind: "canvas" });
      toast.success("Link copied to clipboard");
    } catch {
      toast.error("Failed to copy link");
    }
  }, [publicUrl]);

  const handleUpdate = useCallback(() => {
    if (!canEdit || isMutating || isPublishing) return;
    publishCanvas.mutate(canvas.id);
  }, [canEdit, isMutating, isPublishing, canvas.id, publishCanvas]);

  const statusText = failedFirstPublish
    ? "Publish failed"
    : linkSubtitle(status, publishedAt, linkOn);

  return (
    <section className="flex flex-col gap-2" data-testid="canvas-link-access">
      <p className="text-sm font-medium text-n-70">Link access</p>
      <div className="flex min-h-10 items-center gap-2.5 px-1">
        <span className="inline-flex size-8 shrink-0 items-center justify-center rounded-full bg-n-10 text-n-60">
          <Link2 className="size-3.5" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-base text-n-90">
            Anyone with the link
          </span>
          <span
            className={cn(
              // w-fit so .shimmer's background-size tracks the text, not the
              // flex row — a full-width block makes the highlight invisible.
              "block w-fit text-sm text-n-50",
              isPublishing && "shimmer",
            )}
            data-testid="canvas-link-access-status"
          >
            {statusText}
          </span>
        </span>
        {isPublishing ? (
          <span
            className="inline-flex shrink-0 items-center"
            data-testid="canvas-link-access-spinner"
            aria-label="Publishing"
          >
            <Spinner size="md" />
          </span>
        ) : (
          <Switch
            checked={linkOn}
            onCheckedChange={handleToggle}
            disabled={!canEdit || isMutating}
            aria-label="Anyone with the link"
            data-testid="canvas-link-access-switch"
          />
        )}
      </div>
      {(linkOn || failedFirstPublish) && (
        <div className="flex flex-col gap-2 pl-[42px]">
          {failedFirstPublish && canEdit && (
            <p className="text-sm text-spectrum-red">
              Publishing failed.{" "}
              <button
                type="button"
                className="underline underline-offset-2 hover:text-n-90"
                disabled={isMutating}
                onClick={handleUpdate}
              >
                Try again
              </button>
            </p>
          )}
          {linkOn && (
            <div className="flex flex-wrap items-center gap-2">
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={handleCopy}
              >
                <Copy className="size-3.5" />
                Copy link
              </Button>
              {showUpdate && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  disabled={isMutating}
                  onClick={handleUpdate}
                >
                  {status === "failed" ? "Retry publish" : "Update"}
                </Button>
              )}
            </div>
          )}
        </div>
      )}
    </section>
  );
}
